import { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { ShippingAddress } from '../../types/shipping';
import { shippingAddressService } from '../../services/shippingAddressService';

interface AddressAutocompleteProps {
  type: 'sender' | 'recipient';
  userId: string;
  value: Partial<ShippingAddress>;
  onChange: (address: Partial<ShippingAddress>) => void;
  error?: boolean;
  required?: boolean;
}

export default function AddressAutocomplete({
  type,
  userId,
  value,
  onChange,
  error,
  required
}: AddressAutocompleteProps) {
  const [addresses, setAddresses] = useState<ShippingAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');

  const loadAddresses = useCallback(async () => {
    if (!userId) return;
    
    try {
      const items = await shippingAddressService.listAddresses(userId, type);
      setAddresses(items);
    } catch (error) {
      // Silenciosamente falha e mantém a lista vazia
    } finally {
      setLoading(false);
    }
  }, [userId, type]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const handleChange = (_: any, newValue: ShippingAddress | null) => {
    if (newValue) {
      const { id, userId, createdAt, updatedAt, ...addressData } = newValue;
      onChange(addressData);
    } else {
      onChange({});
    }
  };

  const currentValue = addresses.find(addr => 
    addr.name === value.name && 
    addr.address === value.address &&
    addr.zip_code === value.zip_code
  ) || null;

  const getOptionKey = (option: ShippingAddress) => {
    return `${option.id}-${option.updatedAt}`;
  };

  return (
    <Autocomplete
      options={addresses}
      loading={loading}
      value={currentValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={(option) => `${option.name} - ${option.address}`}
      isOptionEqualToValue={(option, value) => 
        option.name === value.name && 
        option.address === value.address &&
        option.zip_code === value.zip_code
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={type === 'sender' ? 'Selecionar Remetente' : 'Selecionar Destinatário'}
          error={error}
          required={required}
        />
      )}
      renderOption={(props, option) => {
        const { key: _, ...otherProps } = props;
        return (
          <Box 
            component="li" 
            key={getOptionKey(option)}
            {...otherProps}
          >
            <Box>
              <Typography variant="subtitle1">{option.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {option.address}, {option.city} - {option.zip_code}
              </Typography>
            </Box>
          </Box>
        );
      }}
    />
  );
}
